import React, { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../../shared/stores/useAuthStore';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import {
  AUTH_SESSION_CLEARED_EVENT,
  clearAuthSessionStorage,
} from '../../../shared/lib/authSession';
import { superAdminApi } from '../api/superAdminApi';
import { AdminAccessMessage, AdminLoginSurface } from './AdminAccessSurface';
import './styles/adminTokens.css';
import './styles/adminShell.css';

interface SuperAdminGuardProps {
  children: React.ReactNode;
}

interface AdminSessionUser {
  id: string;
  role: string;
  username: string;
}

type VerificationStatus = 'checking' | 'failed' | 'verified';

interface VerificationState {
  status: VerificationStatus;
  token: string | null;
}

interface PendingVerification {
  request: Promise<void>;
  token: string;
}

let pendingVerification: PendingVerification | null = null;

const readClaim = (payload: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string') {
      return value;
    }
  }
  return '';
};

const decodeAdminToken = (token: string): AdminSessionUser | null => {
  try {
    const encoded = token.split('.')[1];
    if (!encoded) {
      return null;
    }
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    return {
      id: readClaim(payload, [
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
        'nameid',
      ]),
      username: readClaim(payload, [
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
        'unique_name',
        'username',
      ]),
      role: readClaim(payload, [
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
        'role',
      ]),
    };
  } catch {
    return null;
  }
};

const messageFrom = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object' && 'message' in value) {
    return typeof value.message === 'string' ? value.message : null;
  }
  return null;
};

const loginErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = error.response;
    if (response && typeof response === 'object' && 'data' in response) {
      return messageFrom(response.data) ?? messageFrom(error) ?? 'Login failed';
    }
  }
  return messageFrom(error) ?? 'Login failed';
};

const getResponseStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const response = error.response;
  if (!response || typeof response !== 'object' || !('status' in response)) {
    return null;
  }
  return typeof response.status === 'number' ? response.status : null;
};

const isAuthorizationFailure = (error: unknown): boolean => {
  const status = getResponseStatus(error);
  return status === 401 || status === 403;
};

const requestAdminVerification = (token: string): Promise<void> => {
  if (pendingVerification?.token === token) {
    return pendingVerification.request;
  }

  const request = Promise.resolve().then(async () => {
    await superAdminApi.getHealth();
  });
  pendingVerification = { request, token };
  const clearPending = () => {
    if (pendingVerification?.request === request) {
      pendingVerification = null;
    }
  };
  void request.then(clearPending, clearPending);
  return request;
};

const invalidateCurrentSession = () => {
  const current = useAuthStore.getState();
  if (current.accessToken || current.user) {
    clearAuthSessionStorage();
  }
};

const useAuthSessionClearSync = () => {
  const syncFromStorage = useAuthStore((state) => state.syncFromStorage);
  useEffect(() => {
    const sync = () => syncFromStorage();
    window.addEventListener(AUTH_SESSION_CLEARED_EVENT, sync);
    return () => window.removeEventListener(AUTH_SESSION_CLEARED_EVENT, sync);
  }, [syncFromStorage]);
};

const useAdminVerification = (enabled: boolean, accessToken: string | null) => {
  const [attempt, setAttempt] = useState(0);
  const [verification, setVerification] = useState<VerificationState>({
    status: 'checking',
    token: null,
  });

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    if (!accessToken) {
      invalidateCurrentSession();
      return undefined;
    }

    let active = true;
    setVerification({ status: 'checking', token: accessToken });
    void requestAdminVerification(accessToken).then(
      () => active && setVerification({ status: 'verified', token: accessToken }),
      (error) => {
        if (!active) {
          return;
        }
        if (isAuthorizationFailure(error)) {
          invalidateCurrentSession();
          return;
        }
        setVerification({ status: 'failed', token: accessToken });
      }
    );
    return () => {
      active = false;
    };
  }, [accessToken, attempt, enabled]);

  return {
    retry: useCallback(() => setAttempt((value) => value + 1), []),
    status: verification.token === accessToken ? verification.status : 'checking',
  };
};

const useAdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await superAdminApi.login(username, password);
      const decoded = decodeAdminToken(result.accessToken);
      if (!decoded || decoded.role !== 'Super') {
        setError('Access denied. Super administrator privileges are required.');
        return;
      }
      localStorage.setItem('accessToken', result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);
      localStorage.setItem('user', JSON.stringify(decoded));
      useAuthStore.setState({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        user: decoded,
        hydrated: true,
      });
    } catch (loginError) {
      setError(loginErrorMessage(loginError));
    } finally {
      setLoading(false);
    }
  };

  return {
    error,
    loading,
    password,
    setPassword,
    setUsername,
    submit,
    username,
  };
};

const SuperAdminGuard: React.FC<SuperAdminGuardProps> = ({ children }) => {
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const hydrated = useAuthStore((state) => state.hydrated);
  const hydrate = useAuthStore((state) => state.hydrate);
  const revitUnavailable = isGrpcRuntimeAvailable();
  const login = useAdminLogin();
  const shouldVerify = hydrated && !revitUnavailable && user?.role === 'Super';
  const verification = useAdminVerification(shouldVerify, accessToken);
  useAuthSessionClearSync();

  useEffect(() => {
    if (revitUnavailable || hydrated) {
      return undefined;
    }
    const timer = window.setTimeout(hydrate, 0);
    return () => window.clearTimeout(timer);
  }, [hydrate, hydrated, revitUnavailable]);

  if (revitUnavailable) {
    return (
      <AdminAccessMessage
        ariaLabel="Super admin availability"
        message="The super admin panel is unavailable when connected to Revit."
        title="Unavailable"
      />
    );
  }

  if (!hydrated) {
    return (
      <AdminAccessMessage
        ariaLabel="Super admin session"
        message="Checking admin session"
        status
        title="PipeEX admin"
      />
    );
  }

  if (!user) {
    return (
      <AdminLoginSurface
        error={login.error}
        loading={login.loading}
        onPasswordChange={login.setPassword}
        onSubmit={login.submit}
        onUsernameChange={login.setUsername}
        password={login.password}
        username={login.username}
      />
    );
  }

  if (user.role !== 'Super') {
    return (
      <AdminAccessMessage
        ariaLabel="Super admin access denied"
        message="You need Super Admin privileges to access this page."
        title="Access denied"
      />
    );
  }

  if (verification.status === 'failed') {
    return (
      <AdminAccessMessage
        actionLabel="Retry verification"
        ariaLabel="Super admin session verification"
        message="Unable to verify the admin session."
        onAction={verification.retry}
        title="Connection unavailable"
      />
    );
  }

  if (verification.status !== 'verified') {
    return (
      <AdminAccessMessage
        ariaLabel="Super admin session verification"
        message="Verifying admin permissions"
        status
        title="PipeEX admin"
      />
    );
  }

  return <>{children}</>;
};

export default SuperAdminGuard;
